/**
 * Credit Note Service
 * Handles extraction and validation of CFDI tipo E (Egreso) credit notes
 * Required for Pronto Pago to document the financial cost discount
 *
 * Strategy:
 * 1. Parse XML with DOMParser (handles namespaces, attribute order, encoding)
 * 2. If DOMParser fails, fall back to AI extraction via /api/extract-document
 * 3. Validate extracted data against the parent invoice
 */
import { logger } from '@/lib/logger';

import { CreditNoteData, CreditNoteValidation } from '../types/invoice';
import { API_BASE_URL } from '@/constants/config';

// ─── DOMParser-based extraction ──────────────────────────────────────────────

function getAttr(el: Element, name: string): string {
  return el.getAttribute(name) ?? '';
}

function queryNS(el: Element, localName: string): Element | null {
  const byTag = el.getElementsByTagNameNS('*', localName);
  return byTag.length > 0 ? byTag[0] : null;
}

function queryAllNS(el: Element, localName: string): Element[] {
  return Array.from(el.getElementsByTagNameNS('*', localName));
}

function extractWithDOMParser(content: string): CreditNoteData | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    return null;
  }

  const comprobante = doc.documentElement;

  const tipoComprobante = (getAttr(comprobante, 'TipoDeComprobante') || '').toUpperCase();
  const fecha = getAttr(comprobante, 'Fecha');
  const folio = getAttr(comprobante, 'Folio') || undefined;
  const serie = getAttr(comprobante, 'Serie') || undefined;

  // Amounts
  const subtotal = parseFloat(getAttr(comprobante, 'SubTotal')) || 0;
  const total = parseFloat(getAttr(comprobante, 'Total')) || 0;
  const currency = getAttr(comprobante, 'Moneda') || 'MXN';

  // Emisor
  const emisor = queryNS(comprobante, 'Emisor');
  const issuerRfc = (emisor ? getAttr(emisor, 'Rfc') : '').toUpperCase();
  const issuerName = emisor ? getAttr(emisor, 'Nombre') : undefined;

  // UUID from TimbreFiscalDigital
  let uuid = '';
  const tfd = queryNS(comprobante, 'TimbreFiscalDigital');
  if (tfd) uuid = getAttr(tfd, 'UUID').toUpperCase();
  const certificationDate = tfd ? getAttr(tfd, 'FechaTimbrado') : undefined;

  if (!uuid || !issuerRfc) {
    return null;
  }

  // Related UUID from CfdiRelacionados > CfdiRelacionado
  const tipoRelacionEl = queryNS(comprobante, 'CfdiRelacionados');
  const tipoRelacion = tipoRelacionEl ? getAttr(tipoRelacionEl, 'TipoRelacion') : '01';

  let relatedUuid = '';
  const cfdiRels = queryAllNS(comprobante, 'CfdiRelacionado');
  for (const rel of cfdiRels) {
    const id = getAttr(rel, 'UUID').toUpperCase();
    if (id && /^[A-Fa-f0-9-]{36}$/i.test(id)) {
      relatedUuid = id;
      break;
    }
  }

  // Total tax from Impuestos
  let totalTax = 0;
  const impuestos = queryNS(comprobante, 'Impuestos');
  if (impuestos) {
    const trasladados = getAttr(impuestos, 'TotalImpuestosTrasladados');
    if (trasladados) totalTax = parseFloat(trasladados);
  }

  return {
    uuid,
    folio,
    series: serie,
    issuerRfc,
    issuerName: issuerName || undefined,
    relatedUuid,
    tipoRelacion,
    subtotal,
    totalTax,
    totalAmount: total,
    currency,
    issueDate: fecha ? fecha.split('T')[0] : '',
    certificationDate: certificationDate || undefined,
    tipoComprobante,
  };
}

// ─── AI fallback extraction ──────────────────────────────────────────────────

async function extractWithAI(xmlContent: string): Promise<CreditNoteData | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/extract-document`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xmlContent, documentType: 'credit_note' }),
    });

    if (!response.ok) {
      logger.error('AI extraction failed', { status: response.status });
      return null;
    }

    const result = await response.json();
    if (!result.success || !result.data) return null;

    const d = result.data;
    return {
      uuid: (d.uuid || '').toUpperCase(),
      folio: d.folio || undefined,
      series: d.series || undefined,
      issuerRfc: (d.issuerRfc || '').toUpperCase(),
      issuerName: d.issuerName || undefined,
      relatedUuid: (d.relatedUuid || '').toUpperCase(),
      tipoRelacion: d.tipoRelacion || '01',
      subtotal: Number(d.subtotal) || 0,
      totalTax: Number(d.totalTax) || 0,
      totalAmount: Number(d.totalAmount) || 0,
      currency: d.currency || 'MXN',
      issueDate: d.issueDate || '',
      certificationDate: d.certificationDate || undefined,
      tipoComprobante: (d.tipoComprobante || '').toUpperCase(),
    };
  } catch (error) {
    logger.error('AI credit note extraction error', { error });
    return null;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Extract credit note data from CFDI XML content.
 * Uses DOMParser first, falls back to AI if parsing fails.
 */
export const extractCreditNoteData = async (xmlFile: File): Promise<CreditNoteData | null> => {
  try {
    const content = await xmlFile.text();

    const domResult = extractWithDOMParser(content);
    if (domResult) {
      return domResult;
    }

    return await extractWithAI(content);
  } catch (error) {
    logger.error('Error extracting credit note data', { error });
    return null;
  }
};

/**
 * Validate credit note against the main invoice.
 */
export const validateCreditNote = (
  creditNoteData: CreditNoteData | null,
  invoiceUuid: string,
  invoiceIssuerRfc: string,
  expectedFeeAmount: number,
  tolerancePercent: number = 0.05
): CreditNoteValidation => {
  const errors: string[] = [];

  if (!creditNoteData) {
    return {
      isValid: false,
      errors: ['No se pudo extraer la información de la nota de crédito'],
    };
  }

  if (creditNoteData.tipoComprobante !== 'E') {
    errors.push(
      `El tipo de comprobante debe ser "E" (Egreso). Se encontró: "${creditNoteData.tipoComprobante || 'vacío'}"`
    );
  }

  if (!creditNoteData.relatedUuid) {
    errors.push('La nota de crédito no tiene un UUID relacionado (CfdiRelacionado)');
  } else if (creditNoteData.relatedUuid.toUpperCase() !== invoiceUuid.toUpperCase()) {
    errors.push(
      `El UUID relacionado no coincide con la factura. ` +
        `Esperado: ${invoiceUuid}, Encontrado: ${creditNoteData.relatedUuid}`
    );
  }

  if (creditNoteData.issuerRfc.toUpperCase() !== invoiceIssuerRfc.toUpperCase()) {
    errors.push(
      `El RFC del emisor no coincide con la factura. ` +
        `Esperado: ${invoiceIssuerRfc}, Encontrado: ${creditNoteData.issuerRfc}`
    );
  }

  if (expectedFeeAmount > 0) {
    const minAmount = expectedFeeAmount * (1 - tolerancePercent);
    const maxAmount = expectedFeeAmount * (1 + tolerancePercent);
    if (creditNoteData.totalAmount < minAmount || creditNoteData.totalAmount > maxAmount) {
      errors.push(
        `El monto de la nota de crédito no coincide con el costo financiero. ` +
          `Esperado: $${expectedFeeAmount.toFixed(2)}, Encontrado: $${creditNoteData.totalAmount.toFixed(2)}`
      );
    }
  }

  if (creditNoteData.tipoRelacion !== '01') {
    errors.push(
      `El tipo de relación debe ser "01" (Nota de crédito). Se encontró: "${creditNoteData.tipoRelacion}"`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    data: creditNoteData,
  };
};

/**
 * Check if a file is a credit note (CFDI tipo E).
 */
export const isCreditNoteXml = async (xmlFile: File): Promise<boolean> => {
  try {
    const content = await xmlFile.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/xml');
    if (doc.querySelector('parsererror')) {
      return !!content.match(/TipoDeComprobante\s*=\s*["']E["']/i);
    }
    const tipo = doc.documentElement.getAttribute('TipoDeComprobante');
    return tipo?.toUpperCase() === 'E';
  } catch {
    return false;
  }
};

/**
 * Get a human-readable description of the credit note.
 */
export const getCreditNoteDescription = (data: CreditNoteData): string => {
  const parts = [
    `Nota de Crédito ${data.uuid}`,
    data.folio ? `Folio: ${data.folio}` : null,
    `Monto: $${data.totalAmount.toFixed(2)} ${data.currency}`,
    `Fecha: ${data.issueDate}`,
    `Relacionada a: ${data.relatedUuid}`,
  ].filter(Boolean);

  return parts.join(' | ');
};
